/*
 *
 *  * (C) Copyright 2006-2020 hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * All Rights Reserved.
 *  *
 *  * These materials are unpublished, proprietary, confidential source code of
 *  * hSenid Mobile Solutions (Pvt) Limited and constitute a TRADE SECRET
 *  * of hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * hSenid Mobile Solutions (Pvt) Limited retains all title to and intellectual
 *  * property rights in these materials.
 *  *
 *
 */
import { TOP_CAROUSEL_IDS, AD_CAROUSEL_IDS } from "../constants";
import { apiBasePath } from "../config";

let JSON_HEADER = {
  "Content-Type": "application/json",
};

function processJsonBody(json: { [key: string]: any }) {
  if (json.error) {
    const err: any = new Error(json.error);
    err.status = json.status;
    return Promise.reject(err);
  }
  return json;
}

//Homepage
/*-------------------------------------------------------------------------------*/

export interface UserProfile {
  gender: string;
  name: {
    title: string;
    first: string;
    last: string;
  };
}

export interface UserProfileResp {
  results: UserProfile[];
}

//Editors recommends Homepage
/*-------------------------------------------------------------------------------*/

export interface App {
  id: string;
  appBanner: string;
  appCreatedDate: Date;
  appId: string;
  appLogo: string;
  appType: string;
  categoryId: string[];
  categoryName: string[];
  detailedDescription: string;
  developerName: string;
  downloadCount: number;
  imageUrls: string[];
  instruction: string;
  name: string;
  publishedDateTime: Date;
  rating: number;
  usage: number;
  spId: string;
  status: string;
  summarizedDescription: string;
  ncses: ncse[];
  buildfile: Buildfile;
}

export interface ncse {
  status: string;
  ncsType: string;
  operator: string;
  free: boolean;
  frequency: string;
  amount: string;
  currencyCode: string;
  mo: AllowedPaymentInstruments;
  mt: AllowedPaymentInstruments;
}

export interface Buildfile {
  id: string;
  updatedDate: Date;
  status: string;
  appFileName: string;
  version: string;
  description: string;
  name: string;
  updatedBy: string;
  platformId: string;
  spId: string;
  appId: string;
  appFileId: string;
  appFilePath: string;
}

export interface AllowedPaymentInstruments {
  free: boolean;
  currencyCode: string;
  amount: number;
}

export interface RecommendedAppsReq {
  msisdn: string;
}

export async function getRecommendedApps(
  payload: RecommendedAppsReq
): Promise<SearchResp> {
  const resp = await fetch(
    `${apiBasePath}/appstore/recommendations/${payload.msisdn}`,
    {
      method: "GET",
      headers: {
        ...JSON_HEADER,
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    }
  );
  const json = await resp.json();
  return processJsonBody(json) as SearchResp;
}

//Categories Homepage
/*-------------------------------------------------------------------------------*/
export interface AppCategory {
  categoryName: string;
  icon: string;
}

export interface AppCategoryResp {
  results: App[];
}

export async function getAppCategories(): Promise<AppCategoryResp> {
  const resp = await fetch(`${apiBasePath}/appstore/categories`, {
    method: "GET",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  const json = await resp.json();
  return processJsonBody(json) as AppCategoryResp;
}

export interface OTPRequestBody {
  otpRequest: {
    msisdn: string;
  };
}

export interface OTPRequestRespPayload {
  requestId: string;
  requestCorrelator: string;
  statusCode: string;
  statusDescription: string;
}

export interface OTPRequestResp {
  statusCode: string;
  statusMessage: string;
  debugMessage?: string;
  payload: OTPRequestRespPayload;
}

export async function requestOTP(msisdn: string): Promise<OTPRequestResp> {
  const resp = await fetch(`${apiBasePath}/login/otp/request`, {
    method: "POST",
    headers: JSON_HEADER,
    body: JSON.stringify({ msisdn }),
  });
  const json = await resp.json();
  return processJsonBody(json) as OTPRequestResp;
}

//otp verification
/*------------------------------------------------------------------------*/

export interface OTPVerifyBody {
  otp: string;
  requestId: string;
  requestCorrelator: string;
  firebaseToken?: string;
}

export interface OTPVerifyResp {
  statusCode: string;
  statusMessage: string;
  debugMessage?: string;
  payload: {
    token: string;
    appUser: AppUser;
    requestId: string;
    msisdn: string;
    statusCode: string;
    statusDescription: string;
  };
}

export async function verifyOTP(
  payload: OTPVerifyBody
): Promise<OTPVerifyResp> {
  const resp = await fetch(`${apiBasePath}/login/otp/verify`, {
    method: "POST",
    headers: JSON_HEADER,
    body: JSON.stringify(payload),
  });
  const json = await resp.json();
  return processJsonBody(json) as OTPVerifyResp;
}

//signout
/*------------------------------------------------------------------------*/

export interface SignOutResp {
  statusCode: string;
  statusMessage: string;
  debugMessage?: string;
}

export async function signOutUser(): Promise<SignOutResp> {
  const resp = await fetch(`${apiBasePath}/appstore/logout`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  const json = await resp.json();
  return processJsonBody(json) as SignOutResp;
}

//update user
/*------------------------------------------------------------------------*/

export interface UpdateUserResp {
  statusCode: string;
  statusMessage: string;
  debugMessage?: string;
  payload: AppUser;
}

export async function updateUser(
  appUser: AppUser,
  token: string
): Promise<UpdateUserResp> {
  const resp = await fetch(`${apiBasePath}/appuser/create`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(appUser),
  });
  const json = await resp.json();

  return processJsonBody(json) as UpdateUserResp;
}

//App Details Page
/*-------------------------------------------------------------------------------*/

export interface AppDetailsResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: App;
}

export async function getAppDetails(appId: string): Promise<AppDetailsResp> {
  const resp = await fetch(`${apiBasePath}/appstore/app-data/${appId}`, {
    method: "GET",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  const json = await resp.json();
  return processJsonBody(json) as AppDetailsResp;
}

export async function fromDeveloper(appId: string): Promise<SearchResp> {
  const resp = await fetch(`${apiBasePath}/appstore/from-developer/${appId}`, {
    method: "GET",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  const json = await resp.json();
  return processJsonBody(json) as SearchResp;
}

//Search Results Page
/*--------------------------------------------------------------------*/

export interface SearchResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: App[];
  totalPages: number;
  pageNumber: number;
  totalElements: number;
}

export interface SearchReq {
  pageNo: number;
  pageSize: number;
  body: {
    search?: string;
    categoryName?: number;
    classification?: string;
  };
}

export async function getSearchResult(payload: SearchReq): Promise<SearchResp> {
  const resp = await fetch(
    `${apiBasePath}/appstore/search?pageNo=${payload.pageNo}&pageSize=${payload.pageSize}`,
    {
      method: "POST",
      headers: {
        ...JSON_HEADER,
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
      body: JSON.stringify(payload.body),
    }
  );
  const json = await resp.json();
  return processJsonBody(json) as SearchResp;
}

//My Apps Page
/*-----------------------------------------------------*/

export interface MyApp {
  appId: string;
  name: string;
  currentStatus: string;
  lastRegisteredDate: string;
  rating: number;
  chargingAmount: string | null;
  frequency: string | null;
}

export interface MyAppsResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: MyApp[];
}

export interface MyAppsReq {
  senderAddress: string;
}

export interface UnsubscribeReq {
  msisdn: string;
  appId: string;
}

export interface UnsubscribeResp {
  statusCode: string;
  statusMessage: string;
}

export async function getMyApps(
  payload: MyAppsReq,
  token: string
): Promise<MyAppsResp> {
  const resp = await fetch(`${apiBasePath}/appstore/my-apps`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(payload),
  });
  const json = await resp.json();
  return processJsonBody(json) as MyAppsResp;
}

export async function unsubscribeMyApps(
  payload: UnsubscribeReq,
  token: string
): Promise<UnsubscribeResp> {
  const resp = await fetch(
    `${apiBasePath}/appstore/my-apps/${payload.msisdn}/unsubscribe?app=${payload.appId}`,
    {
      method: "POST",
      headers: {
        ...JSON_HEADER,
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    }
  );
  const json = await resp.json();
  return processJsonBody(json) as UnsubscribeResp;
}

//App Reviews Page
/*-----------------------------------------------------*/
export interface AppUser {
  id: string | null;
  msisdn: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  identity: string | null;
  postalAddress: string | null;
  displayPicture: string | null;
}

export interface AppReview {
  id: string;
  appId: string;
  comment: string;
  reviewDate: string;
  msisdn: string;
  name: string;
}

export interface AppReviewResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: AppReview;
}

export async function submitReview(
  payload: AppReview,
  token: string
): Promise<AppReviewResp> {
  return fetch(`${apiBasePath}/appstore/reviews/add`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(payload),
  })
    .then((resp) => {
      const json = resp.json();
      return processJsonBody(json) as AppReviewResp;
    })
    .catch((err) => {
      console.log("Error while adding review: ", err);
      return err;
    });
}

export interface ReviewsResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: AppReview[];
}

export async function getReviews(appId: string): Promise<AppReviewResp> {
  const resp = await fetch(`${apiBasePath}/appstore/reviews/${appId}`, {
    method: "GET",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });
  const json = await resp.json();
  return processJsonBody(json) as AppReviewResp;
}

//App Ratings
/*-----------------------------------------------------*/

export interface AppRating {
  id?: string;
  appId: string;
  rating: number;
  lastUpdated?: string;
  msisdn: string;
}

export interface UserRatingRequest {
  appId: string;
  msisdn: string;
}

export interface UserRatingResponse {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: AppRating;
}

export async function getUserRating(
  userRatingReq: UserRatingRequest
): Promise<UserRatingResponse> {
  const resp = await fetch(`${apiBasePath}/appstore/rating/filter`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(userRatingReq),
  });
  const json = await resp.json();
  return processJsonBody(json) as UserRatingResponse;
}

export async function addAppRating(
  appRating: AppRating,
  token: string
): Promise<App> {
  const resp = await fetch(`${apiBasePath}/appstore/rating/add`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(appRating),
  });
  const json = await resp.json();
  return processJsonBody(json) as App;
}

//Subscribe/unsubscribe
/*-----------------------------------------------------*/

export interface SubscriptionRequest {
  appId: string;
  msisdn: string;
  action: number;
}

export interface SdpSubResponse {
  subscriptionStatus: string;
  statusDetail: string;
  version: string;
  statusCode: string;
}

export interface SubscriptionResponse {
  statusCode: string;
  statusMessage: string;
  debugMessage: string;
  payload: SdpSubResponse;
}

export async function subscribe(
  subReq: SubscriptionRequest,
  token: string
): Promise<SubscriptionResponse> {
  const body = {
    applicationId: subReq.appId,
    subscriberId: `tel:${subReq.msisdn}`,
    action: subReq.action,
  };
  const resp = await fetch(`${apiBasePath}/appstore/subscribe`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(body),
  });
  const json = await resp.json();
  return processJsonBody(json) as SubscriptionResponse;
}

export async function getSubsptionStatus(
  subReq: UserRatingRequest
): Promise<SubscriptionResponse> {
  const body = {
    applicationId: subReq.appId,
    subscriberId: `tel:${subReq.msisdn}`,
  };
  const resp = await fetch(`${apiBasePath}/appstore/subscription-status`, {
    method: "POST",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
    body: JSON.stringify(body),
  });
  const json = await resp.json();
  return processJsonBody(json) as SubscriptionResponse;
}

/**
 * get carousel image
 */

export interface CarouselSlot {
  id?: string;
  slotId: TOP_CAROUSEL_IDS | AD_CAROUSEL_IDS;
  imagePathFragment: string;
  appId?: string | null;
  appName?: string;
  developerName?: string;
  rating?: number;
  detailedDescription?: string;
  summarizedDescription?: string;
}

export interface GetCarouselImageResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string | null;
  payload: CarouselSlot[];
}

export async function getCarouselImages(): Promise<GetCarouselImageResp> {
  const resp = await fetch(`${apiBasePath}/appstore/carouselSlots`, {
    method: "GET",
    headers: {
      ...JSON_HEADER,
      Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    },
  });

  const json = await resp.json();
  return processJsonBody(json) as GetCarouselImageResp;
}

//Get Apps by Specification
/*--------------------------------------------------------------------*/

export interface AppsBySpecificationReq {
  pageNo: number;
  pageSize: number;
  specification: string;
}

export interface AppsBySpecificationResp {
  statusCode: string;
  statusMessage: string;
  payload: App[];
  totalPages: number;
  pageNumber: number;
  totalElements: number;
}

export async function getAppsBySpecification(
  payload: AppsBySpecificationReq
): Promise<AppsBySpecificationResp> {
  const resp = await fetch(
    `${apiBasePath}/appstore/specifications?pageNo=${payload.pageNo}&pageSize=${payload.pageSize}&specification=${payload.specification}`,
    {
      method: "GET",
      headers: {
        ...JSON_HEADER,
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    }
  );
  const json = await resp.json();
  return processJsonBody(json) as AppsBySpecificationResp;
}

/**
 * Get app of the week
 */

export interface GetAppOfTheWeekResp {
  statusCode: string;
  statusMessage: string;
  debugMessage: string | null;
  payload: {
    appId: string;
  };
}

export async function getAppOfTheWeek(): Promise<GetAppOfTheWeekResp> {
  const resp = await fetch(`${apiBasePath}/appstore/app-of-the-week`, {
    method: "GET",
    headers: JSON_HEADER,
  });

  const json = await resp.json();
  return processJsonBody(json) as GetAppOfTheWeekResp;
}

/**
 * Download buildfile
 */

export interface BuildfileInfo {
  fileName: string;
  spId: string;
  appId: string;
}

export interface BuildfileDownloadResp {
  statusCode?: string;
  statusMessage?: string;
  debugMessage?: string | null;
  fileName?: string;
  status?: number;
  payload: ArrayBuffer | null;
}

export async function getBuildfile({
  fileName,
  spId,
  appId,
}: BuildfileInfo): Promise<BuildfileDownloadResp> {
  const resp = await fetch(
    `${apiBasePath}/appstore/build-file/download?buildfile=${fileName}&spId=${spId}&appId=${appId}`,
    {
      method: "GET",
      headers: {
        ...JSON_HEADER,
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
      },
    }
  );

  if (resp.headers.get("Content-Type") === "application/octet-stream") {
    return resp.arrayBuffer().then((arrBuffer) => {
      return {
        fileName,
        status: resp.status,
        payload: arrBuffer,
      };
    });
  } else {
    const json = await resp.json();
    return processJsonBody(json) as BuildfileDownloadResp;
  }
}
